import { Platform } from 'react-native'
import {
  dedupeSavedAccountSessions,
  hasDuplicateSavedAccountSessions,
  normalizeAccountEmail,
} from './accountSessions.helper'
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types'

/**
 * One stored Zporti account (auth identity).
 * Persisted on device so users can switch between two Google accounts without signing out.
 */
export interface SavedAccountSession {
  userId: string
  email: string
  /** From Google OAuth user_metadata.full_name / name */
  displayName: string | null
  /** From Google OAuth user_metadata.avatar_url (Google profile picture). */
  avatarUrl: string | null
  /** Zporti game avatar ID — populated after fetching /users/profile/:userId. Used as fallback image. */
  zportiAvatarId: string | null
  /**
   * Zporti custom layered look — populated alongside zportiAvatarId from the profile fetch.
   * Non-null overrides zportiAvatarId so an inactive account in the switcher shows its custom look.
   */
  zportiAvatarConfig: EquippedMap | null
  accessToken: string
  refreshToken: string
  /** Unix ms — 0 means unknown. Supabase refresh token lasts weeks, so this is just for display. */
  expiresAt: number
}

const STORAGE_KEY = 'zporti_saved_accounts'

async function readRaw(): Promise<string | null> {
  if (Platform.OS === 'web') {
    if (typeof window === 'undefined') return null
    try {
      return window.localStorage.getItem(STORAGE_KEY)
    } catch {
      return null
    }
  }
  const AsyncStorage = require('@react-native-async-storage/async-storage').default
  return AsyncStorage.getItem(STORAGE_KEY)
}

async function writeRaw(value: string): Promise<void> {
  if (Platform.OS === 'web') {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(STORAGE_KEY, value)
    } catch {
      /* quota / private mode */
    }
    return
  }
  const AsyncStorage = require('@react-native-async-storage/async-storage').default
  await AsyncStorage.setItem(STORAGE_KEY, value)
}

export async function getAllSavedSessions(
  activeUserId?: string | null,
): Promise<SavedAccountSession[]> {
  try {
    const raw = await readRaw()
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const sessions = parsed as SavedAccountSession[]
    const deduped = dedupeSavedAccountSessions(sessions, activeUserId)
    if (hasDuplicateSavedAccountSessions(sessions)) {
      await writeRaw(JSON.stringify(deduped))
    }
    return deduped
  } catch {
    return []
  }
}

export async function saveSession(session: SavedAccountSession): Promise<void> {
  try {
    const all = await getAllSavedSessions()
    const normalizedEmail = normalizeAccountEmail(session.email)
    const withoutDuplicates = all.filter((stored) => {
      if (stored.userId === session.userId) return false
      if (!normalizedEmail) return true
      return normalizeAccountEmail(stored.email) !== normalizedEmail
    })
    withoutDuplicates.push(session)
    await writeRaw(JSON.stringify(dedupeSavedAccountSessions(withoutDuplicates)))
  } catch {
    /* non-fatal */
  }
}

export async function getStoredSession(userId: string): Promise<SavedAccountSession | null> {
  const all = await getAllSavedSessions()
  return all.find((s) => s.userId === userId) ?? null
}

export async function removeStoredSession(userId: string): Promise<void> {
  try {
    const all = await getAllSavedSessions()
    await writeRaw(JSON.stringify(all.filter((s) => s.userId !== userId)))
  } catch {
    /* non-fatal */
  }
}
